// @mui
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Box, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useSettingsContext } from 'src/components/settings';
import { buildMenuTree } from 'src/utils/menuUtils';

// ----------------------------------------------------------------------

export default function NavDesktop() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { themeMode } = useSettingsContext();
  const { user } = useAuthContext();
  const theme = useTheme();

  const fill = {
    default: themeMode === 'dark' ? '#919EAB' : '#999999',
    active: theme.palette.primary.main,
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpenMenuId(id);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpenMenuId(null);
  };

  // API 응답으로 받은 menuList를 계층 구조로 변환
  const menuTree = useMemo(() => {
    if (!user?.menuList) return [];
    return buildMenuTree(user.menuList);
  }, [user?.menuList]);

  return (
    <Stack component="nav" direction="row">
      <Stack direction={'row'} spacing={4} sx={{ '& *': { transition: 'all 0.2s' } }}>
        {menuTree.map((item, index: number) => {
          const isOpen = openMenuId === item.path;
          const hasChildren = item.children && item.children.length > 0;

          return (
            <Stack key={`menu-${index}`} direction={'row'} gap={1}>
              <Box
                width={'100%'}
                component={'nav'}
                sx={{ display: 'flex', lineHeight: '1.2', cursor: 'pointer', gap: 1 }}
                onClick={(e) => {
                  if (hasChildren) {
                    handleClick(e, item.path);
                  } else {
                    handleClose();
                    !pathname.includes(item.path) && navigate(item.path);
                  }
                }}
              >
                <Typography
                  variant={'body1'}
                  component={'nav'}
                  sx={{
                    color:
                      pathname === '/' && item.path === '/fc-chart'
                        ? fill.active
                        : pathname.includes(item.path)
                        ? fill.active
                        : fill.default,
                    fontWeight:
                      pathname === '/' && item.path === '/fc-chart'
                        ? 700
                        : pathname.includes(item.path)
                        ? 700
                        : 400,
                    wordBreak: 'keep-all',
                  }}
                >
                  {item.title}
                </Typography>
              </Box>

              <Stack
                component="nav"
                sx={{
                  display: 'flex',
                  lineHeight: '1.2',
                  cursor: 'pointer',
                  position: 'absolute',
                  top: '80%',
                }}
              >
                {hasChildren && (
                  <Menu
                    open={isOpen}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    onClick={handleClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        mt: 1.5,
                        transform: 'translateX(20%) !important',
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    {item.children?.map((child, j) => (
                      <MenuItem
                        key={'child' + j}
                        onClick={() => {
                          navigate(child.path);
                          handleClose();
                        }}
                      >
                        <Typography
                          color={theme.palette.text.secondary}
                          p={0.5}
                          sx={{
                            color: pathname.includes(child.path)
                              ? theme.palette.text.primary
                              : fill.default,
                            fontWeight: pathname.includes(child.path) ? 600 : 400,
                          }}
                        >
                          {child.title}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
